import { SITE_NAME } from "lib/constants";
import NextHeadSeo from "next-head-seo";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { VFC } from "react";
import { getLocale, getServerBaseUrl } from "~/lib/utils/urlHelper";
import { Project } from "~/models/project";
import { Topic } from "~/models/topic";
import { pageCanonicalUrl } from "faqSrc/services/metaInfo/pageCanonicalUrl";
import { pageLanguageAlternates } from "faqSrc/services/metaInfo/pageLanguageAlternates";
import {
  pageOgImage,
  pageOgImageHeight,
  pageOgImageWidth,
} from "faqSrc/services/metaInfo/pageOgImage";

interface Props {
  // please specify an asPath for the page using ISR , because router.asPath doesn't work properly in ISR
  // its value is the same as router.asPath
  // asPath is used to generate meta url, canonical url, etc
  articleSection?: string;
  asPath?: string;
  author?: string;
  baseUrl?: string;
  creatorTwitterAccount?: string;
  description?: string;
  isNoIndexPage?: boolean;
  isQaDetailPage?: boolean;
  keywords?: string[];
  languageAlts?: string[];
  modifiedAtISOString?: string;
  nextHref?: string;
  ogImageUrl?: string;
  prevHref?: string;
  project?: Project;
  publishedDate?: string;
  publishedAtISOString?: string;
  siteTwitterAccount?: string;
  title: string;
  topicId?: Topic["id"];
}

function generateCustomMetaTags({
  articleSection,
  author,
  creatorTwitterAccount,
  description,
  isQaDetailPage,
  keywords,
  modifiedAtISOString,
  pageImage,
  publishedAtISOString,
  siteTwitterAccount,
  title,
}: {
  articleSection?: string;
  author?: string;
  creatorTwitterAccount?: string;
  description?: string;
  isQaDetailPage?: boolean;
  keywords?: string[];
  modifiedAtISOString?: string;
  pageImage: string;
  publishedAtISOString?: string;
  siteTwitterAccount?: string;
  title: string;
}): Record<string, string>[] | [] {
  return [
    ...(articleSection
      ? [
          {
            content: articleSection,
            property: "article:section",
          },
        ]
      : []),
    ...(author ? [{ content: author, name: "author" }] : []),
    ...(author && isQaDetailPage
      ? [{ content: author, property: "article:author" }]
      : []),
    { content: "ja_JP", property: "og:locale" },
    ...(creatorTwitterAccount
      ? [
          {
            content: `@${creatorTwitterAccount}`,
            name: "twitter:creator",
          },
        ]
      : []),
    ...(description && pageImage
      ? [
          {
            content: description,
            name: "twitter:description",
          },
        ]
      : []),
    ...(keywords?.length
      ? [
          {
            content: keywords.join(","),
            name: "keywords",
          },
        ]
      : []),
    ...(keywords?.length && isQaDetailPage
      ? keywords.map((k) => ({
          content: k,
          property: "article:tag",
        }))
      : []),
    ...(modifiedAtISOString
      ? [
          {
            content: modifiedAtISOString,
            property: "article:modified_time",
          },
        ]
      : []),
    // og:image is output at default due to next-head-seo behavior.
    ...(pageImage
      ? [
          { content: "summary_large_image", name: "twitter:card" },
          { content: pageOgImageWidth(), property: "og:image:width" },
          { content: pageOgImageHeight(), property: "og:image:height" },
        ]
      : []),
    ...(publishedAtISOString
      ? [
          {
            content: publishedAtISOString,
            property: "article:published_time",
          },
        ]
      : []),
    ...(siteTwitterAccount
      ? [
          {
            content: `@${siteTwitterAccount}`,
            name: "twitter:site",
          },
        ]
      : []),
    ...(title && pageImage
      ? [
          { content: title, name: "twitter:title" },
          { content: title, property: "og:image:alt" },
        ]
      : []),
  ];
}

export const MetaTag: VFC<Props> = ({
  articleSection,
  asPath,
  author,
  baseUrl = undefined,
  creatorTwitterAccount,
  description,
  isNoIndexPage = false,
  isQaDetailPage = false,
  keywords,
  modifiedAtISOString,
  languageAlts,
  nextHref,
  ogImageUrl,
  prevHref,
  project,
  publishedDate,
  publishedAtISOString,
  siteTwitterAccount,
  title,
  topicId,
}) => {
  const router = useRouter();
  const url = `${baseUrl || getServerBaseUrl()}${getLocale({
    project,
    router,
  })}${asPath ?? router?.asPath}`;
  const pageImage =
    typeof ogImageUrl === "string" ? ogImageUrl : pageOgImage({ topicId });

  const languageAlternates = pageLanguageAlternates({
    asPath,
    languageAlts,
    project,
  });

  const customLinkTags: Record<string, string>[] = [];
  if (languageAlternates?.length) {
    languageAlternates.map((langAlt) => {
      customLinkTags.push({
        href: langAlt.href,
        hrefLang: langAlt.hrefLang,
        rel: "alternate",
      });
    });
  }

  const customMetaTags = generateCustomMetaTags({
    articleSection,
    author,
    creatorTwitterAccount,
    description,
    isQaDetailPage,
    keywords,
    modifiedAtISOString,
    pageImage,
    publishedAtISOString,
    siteTwitterAccount,
    title,
  });

  return (
    <>
      <NextHeadSeo
        canonical={pageCanonicalUrl({ asPath, baseUrl, project })}
        customLinkTags={customLinkTags}
        customMetaTags={customMetaTags}
        description={description}
        og={{
          description,
          image: pageImage ? pageImage : undefined,
          siteName: project?.siteName ? project.siteName : SITE_NAME,
          title,
          type: isQaDetailPage ? "article" : "website",
          url,
        }}
        robots={isNoIndexPage ? "noindex, nofollow" : "index, follow"}
        title={title}
      />
      <Head>
        <link
          rel="shortcut icon"
          href={project?.faviconPath || "/favicon.ico"}
        />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="apple-touch-icon"
          sizes="57x57"
          href={
            project?.appleIcon57Path || "/img/site-icon/apple-icon-57x57.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="60x60"
          href={
            project?.appleIcon60Path || "/img/site-icon/apple-icon-60x60.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="72x72"
          href={
            project?.appleIcon72Path || "/img/site-icon/apple-icon-72x72.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href={
            project?.appleIcon76Path || "/img/site-icon/apple-icon-76x76.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="114x114"
          href={
            project?.appleIcon114Path || "/img/site-icon/apple-icon-114x114.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href={
            project?.appleIcon120Path || "/img/site-icon/apple-icon-120x120.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href={
            project?.appleIcon144Path || "/img/site-icon/apple-icon-144x144.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href={
            project?.appleIcon152Path || "/img/site-icon/apple-icon-152x152.png"
          }
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={
            project?.appleIcon180Path || "/img/site-icon/apple-icon-180x180.png"
          }
        />
        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href={
            project?.androidIcon192Path ||
            "/img/site-icon/android-icon-192x192.png"
          }
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={project?.favicon32Path || "/img/site-icon/favicon-32x32.png"}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href={project?.favicon96Path || "/img/site-icon/favicon-96x96.png"}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={project?.favicon16Path || "/img/site-icon/favicon-16x16.png"}
        />
        <meta
          name="msapplication-TileImage"
          content={
            project?.msIcon144Path || "/img/site-icon/ms-icon-144x144.png"
          }
        />
        <meta name="msapplication-TileColor" content="#98CB51" />
        <meta name="theme-color" content="#98CB51" />
        {nextHref && <link rel="next" href={nextHref} />}
        {prevHref && <link rel="prev" href={prevHref} />}
        {publishedDate && <meta name="date" content={publishedDate} />}
      </Head>
    </>
  );
};

export const DefaultMetaTag: VFC = () => {
  // TODO: Add Twitter Information
  return (
    <>
      <NextHeadSeo
        customMetaTags={[
          {
            content: "ja_JP",
            property: "og:locale",
          },
        ]}
        description="「聞いて知れる」、Q&Aサイトです。どんな質問にも答えます！"
        title={SITE_NAME}
        canonical={getServerBaseUrl()}
        og={{
          siteName: SITE_NAME,
          type: "website",
          url: getServerBaseUrl(),
        }}
      />
    </>
  );
};
